import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vehicleUnitLines } from '../../../utils/vehicleUnitLabel';
import { ensureOk, toHttpError } from '../../../services/httpError';
import { revokeVehiclePlateFromLpr, revokeVehicleTagFromDevices } from '../../../utils/vehicleRfidSync';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { X, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { fetchWithAuth, condoApi, makeAbsolutePhotoUrl } from '../../../services/api';
import toast from 'react-hot-toast';
import Select, { SingleValue } from 'react-select';
import { cn } from '../../../utils/cn';
import { getUnitLabel, CondoType } from '../../../utils/terminology';
import { UnitSelector, UnitOption } from '../../UnitSelector';
import { TagRadixSelector } from '../../TagRadixSelector';
import { describeTag, RFID_TAG_FORMATS, RFID_TAG_FORMAT_LABELS, RFID_TAG_FORMAT_HINTS, type RfidTagFormat } from '../../../utils/rfidTagFormat';
import { TagRadix, applyTagRadix, isAmbiguousTagValue } from '../../../utils/tagValueRadix';
import { VehiclePhotoField } from '../../vehicles/VehiclePhotoField';
import { LicensePlate } from '../../ui/LicensePlate';

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  identity_photo_url?: string;
}

interface VehicleFormData {
  plate: string;
  rfid_tag?: string;
  model?: string;
  brand?: string;
  color?: string;
  year?: number;
  owner_type: 'employee' | 'visitor' | 'company' | 'resident' | 'service_provider';
  owner_name?: string;
  person_id?: string;
  notes?: string;
}

interface Vehicle {
  id: string;
  plate: string;
  rfid_tag?: string;
  rfid_tag_format?: string;
  model?: string;
  brand?: string;
  color?: string;
  year?: number;
  owner_type?: string;
  owner_name?: string;
  person_id?: string;
  person?: Person;
  unit_id?: string;
  unit?: { id: string; number: string; block?: { id: string; name: string } };
  is_active: boolean;
  notes?: string;
  photo_url?: string;
}

interface VehicleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId: string;
  customerType: 'condominio' | 'empresa';
  editingVehicle?: Vehicle | null;
  preselectedPerson?: Person;
  preselectedOwnerType?: 'resident' | 'employee' | 'visitor' | 'service_provider' | 'company';
  preselectedUnitId?: string;
  preselectedUnitName?: string;
  darkMode?: boolean;
  condoType?: CondoType;
}

export const VehicleFormModal: React.FC<VehicleFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  customerType,
  editingVehicle,
  preselectedPerson,
  preselectedOwnerType,
  preselectedUnitId,
  preselectedUnitName,
  // Defaults dark (not light) — matches the sibling VehicleDetailModal's `lightTheme = false`
  // convention and OperationalView's actual default theme, so a caller that forgets to pass
  // this prop fails safe instead of rendering a jarring white modal over a dark screen.
  darkMode = true,
  condoType,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const blobUrlRef = useRef<string | null>(null); // tracks blob URL for cleanup

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Person[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showPersonSelector, setShowPersonSelector] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Unit/Department filtering
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedUnitOption, setSelectedUnitOption] = useState<UnitOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SingleValue<{ value: string; label: string }>>(null);

  // Ref for proprietario input to auto-focus
  const proprietarioInputRef = useRef<HTMLInputElement>(null);

  // ESC key double-press state
  const [escPressCount, setEscPressCount] = useState(0);
  const escTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, touchedFields }
  } = useForm<VehicleFormData>({
    mode: 'onBlur', // Validate on blur
    defaultValues: {
      owner_type: customerType === 'condominio' ? 'resident' : 'employee'
    }
  });

  const watchedOwnerType = watch('owner_type');
  const watchedPlate = watch('plate') || '';

  // DEC/HEX answer for an all-digit RFID tag (see utils/tagValueRadix). The answer
  // belongs to one specific number, so changing the value drops it; editing keeps the
  // meaning the stored value already has today, so touching an unrelated field can
  // never repoint the tag at a different card.
  const watchedRfidTag = watch('rfid_tag') || '';
  const [rfidRadix, setRfidRadix] = useState<TagRadix | null>(null);
  const rfidRadixValueRef = useRef<string | null>(null);

  // How to read the tag. 'auto' is the historical guess and what every row stored before this
  // feature carries, so it stays the default and keeps the DEC/HEX prompt alive.
  const [rfidTagFormat, setRfidTagFormat] = useState<RfidTagFormat>(
    (editingVehicle?.rfid_tag_format as RfidTagFormat) || 'auto',
  );

  // Live read-back of what the tag resolves to, so a wrong format is visible before saving
  // rather than at a gate. Derived from the single resolved card value, so the four views
  // cannot disagree with each other.
  const rfidTagPreview = describeTag(watchedRfidTag, rfidTagFormat);

  useEffect(() => {
    const trimmed = watchedRfidTag.trim();
    if (rfidRadixValueRef.current === trimmed) return;
    rfidRadixValueRef.current = trimmed;
    const isStoredValue = !!editingVehicle && trimmed === (editingVehicle.rfid_tag || '').trim();
    setRfidRadix(isStoredValue && isAmbiguousTagValue(trimmed) ? 'dec' : null);
  }, [watchedRfidTag, editingVehicle]);

  // Fetch departments for empresa
  const fetchDepartments = useCallback(async () => {
    if (!customerId || customerType !== 'empresa') return;

    try {
      const response = await condoApi.getDepartments(customerId);
      const deptData = response.data?.data || response.data || [];
      setDepartments(deptData);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, [customerId, customerType]);

  // Load departments when modal opens
  useEffect(() => {
    if (open && customerType === 'empresa') {
      fetchDepartments();
    }
  }, [open, customerType, fetchDepartments]);

  // Reset the form when the modal opens, or when a DIFFERENT vehicle is opened in it.
  //
  // Keyed on the vehicle's ID, never on the object. The Veículos page rebuilds that object on
  // every render — a websocket tick, a refetch, a filter change — and this effect calls
  // `setPhotoFile(null)` and restores the preview from the stored record. Keyed on the object it
  // therefore fired repeatedly while the modal sat open, throwing away a photo the operator had
  // just cropped (and anything they had typed) before Salvar was ever pressed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      if (editingVehicle) {
        reset({
          plate: editingVehicle.plate,
          rfid_tag: editingVehicle.rfid_tag || '',
          model: editingVehicle.model || '',
          brand: editingVehicle.brand || '',
          color: editingVehicle.color || '',
          year: editingVehicle.year,
          owner_type: (editingVehicle.owner_type as any) || (customerType === 'condominio' ? 'resident' : 'employee'),
          owner_name: editingVehicle.owner_name || '',
          person_id: editingVehicle.person_id,
          notes: editingVehicle.notes || ''
        });
        setSelectedPerson(editingVehicle.person || null);
        // useState only reads editingVehicle on mount; the modal is reused across vehicles, so
        // without this the previous vehicle's format would stick to the next one.
        // Rows written before this feature carry no format and are auto by definition.
        setRfidTagFormat((editingVehicle.rfid_tag_format as RfidTagFormat) || 'auto');
      } else {
        const defaultOwnerType = preselectedOwnerType || (customerType === 'condominio' ? 'resident' : 'employee');
        reset({
          plate: '',
          rfid_tag: '',
          model: '',
          brand: '',
          color: '',
          year: undefined,
          owner_type: defaultOwnerType,
          owner_name: preselectedPerson ? `${preselectedPerson.first_name} ${preselectedPerson.last_name}` : '',
          person_id: preselectedPerson?.id,
          notes: ''
        });
        setSelectedPerson(preselectedPerson || null);
        // A new vehicle starts at auto, never inheriting the format of whichever vehicle was
        // edited last in this same modal instance.
        setRfidTagFormat('auto');
      }
      setSearchTerm('');
      setSearchResults([]);
      setShowPersonSelector(false);
      setSelectedDepartment(null);
      setSubmitAttempted(false);
      setPhotoFile(null);
      setPhotoRemoved(false);
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      setPhotoPreviewUrl(editingVehicle?.photo_url ? makeAbsolutePhotoUrl(editingVehicle.photo_url) : null);

      const ownerUnit = (editingVehicle as any)?.owner_unit;
      if (editingVehicle?.unit) {
        // Edit mode: pre-select unit from vehicle's unit
        setSelectedUnitOption({ value: editingVehicle.unit.id, label: editingVehicle.unit.number, blockName: editingVehicle.unit.block?.name });
      } else if (ownerUnit?.id) {
        // A person-owned vehicle carries no `unit` of its own — the owner's unit arrives as
        // `owner_unit`. Picking a morador from the search already auto-selects their unit
        // (see the person picker below); reopening the SAME vehicle to edit it did not, so
        // Quadra and Lote came up "— Todos —" / "— Nenhuma —" for a vehicle whose owner has
        // both. Same rule, both ways in.
        setSelectedUnitOption({ value: ownerUnit.id, label: ownerUnit.number, blockName: ownerUnit.block?.name });
      } else if (!editingVehicle && preselectedUnitId) {
        // New vehicle from unit page: lock to preselected unit for resident filtering
        setSelectedUnitOption({ value: preselectedUnitId, label: preselectedUnitName || preselectedUnitId });
      } else {
        setSelectedUnitOption(null);
      }
      setEscPressCount(0);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
        escTimeoutRef.current = null;
      }
    }
  }, [open, editingVehicle?.id, reset, customerType]);

  // Search for people with filters
  useEffect(() => {
    const searchPeople = async () => {
      if (watchedOwnerType === 'company') {
        setSearchResults([]);
        return;
      }

      // Only search if there's a search term, a unit/dept filter, or the dropdown was focused
      const hasSearchTerm = searchTerm.trim().length > 0;
      const hasUnitFilter = selectedUnitOption && watchedOwnerType === 'resident';
      const hasDeptFilter = selectedDepartment && watchedOwnerType === 'employee';

      if (!hasSearchTerm && !hasUnitFilter && !hasDeptFilter && !showPersonSelector) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        // Map owner_type to correct role_type
        let roleType = '';
        switch (watchedOwnerType) {
          case 'employee':
            roleType = 'staff';
            break;
          case 'resident':
            roleType = 'resident';
            break;
          case 'visitor':
            roleType = 'visitor';
            break;
          case 'service_provider':
            roleType = 'service_provider';
            break;
        }

        // Build query parameters
        const params = new URLSearchParams({
          role_type: roleType,
          include_inactive_roles: 'false',
          page: '1',
          page_size: '50' // Increased to show more results when filtering by unit/dept
        });

        // Add search term if provided
        if (hasSearchTerm) {
          params.append('search', searchTerm.trim());
        }

        // Add unit filter for residents
        if (selectedUnitOption && watchedOwnerType === 'resident') {
          params.append('unit_id', selectedUnitOption.value);
        }

        // Add department filter for employees
        if (selectedDepartment && watchedOwnerType === 'employee') {
          params.append('department_id', selectedDepartment.value);
        }

        const response = await fetchWithAuth(
          `/api/v1/customers/${customerId}/people?${params.toString()}`
        );

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.data || data.people || []);

        }
      } catch (error) {
        console.error('Error searching people:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchPeople, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, watchedOwnerType, customerId, selectedUnitOption, selectedDepartment, showPersonSelector]);

  // The owner's address, for display only — the Quadra/Lote pickers carry the value that is
  // saved. `owner_unit` is where a person-owned vehicle keeps it; `unit` is for a vehicle
  // registered directly against a unit.
  const ownerUnitLines = vehicleUnitLines(
    (editingVehicle as any)?.unit ?? (editingVehicle as any)?.owner_unit ?? null,
    condoType,
  );

  const onSubmit = async (data: VehicleFormData) => {
    setSubmitAttempted(true);

    // Validate person selection for non-company vehicles
    // Skip person requirement if vehicle is being registered to a unit (preselected or selected in form)
    const hasUnitContext = !!(preselectedUnitId || editingVehicle?.unit_id || selectedUnitOption || selectedDepartment);
    // A vehicle that ALREADY has an owner is not missing one.
    //
    // `selectedPerson` is restored from `editingVehicle.person`, and the Veículos list does not
    // always embed that object — it carries `person_id`. So editing such a vehicle was refused
    // for "no proprietário" even though the field showed the owner's name, and because the
    // guard returns before the request, the vehicle and any photo cropped alongside it both
    // went nowhere. The id is the ownership; the embedded object is just how it was displayed.
    const hasOwner = !!(selectedPerson || data.person_id || editingVehicle?.person_id);
    if (data.owner_type !== 'company' && !hasOwner && !hasUnitContext) {
      toast.error('Por favor, selecione o proprietário do veículo');
      // Scroll to proprietario field
      proprietarioInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      proprietarioInputRef.current?.focus();
      return;
    }

    // Check for other required fields
    if (!data.plate) {
      toast.error('Placa é obrigatória');
      return;
    }

    const rawRfidTag = (data.rfid_tag || '').trim();
    // Only auto has an ambiguity to resolve; an explicit format already states the base.
    if (rfidTagFormat === 'auto' && isAmbiguousTagValue(rawRfidTag) && !rfidRadix) {
      toast.error('Informe se a tag RFID é decimal ou hexadecimal');
      return;
    }

    // An explicit format that cannot read the value would be saved as a tag that resolves to
    // nothing — silently unsyncable. Catch it here rather than at the gate.
    if (rfidTagFormat !== 'auto' && rawRfidTag && !describeTag(rawRfidTag, rfidTagFormat)) {
      toast.error(`Tag RFID inválida para o formato ${RFID_TAG_FORMAT_LABELS[rfidTagFormat]}`);
      return;
    }

    setIsSubmitting(true);

    try {
      const url = editingVehicle
        ? `/api/v1/customers/${customerId}/vehicles/${editingVehicle.id}`
        : `/api/v1/customers/${customerId}/vehicles`;

      const method = editingVehicle ? 'PUT' : 'POST';

      // Prepare vehicle data
      const vehicleData: Record<string, any> = {
        ...data,
        plate: data.plate.toUpperCase(),
        // In auto mode a hex answer is recorded by padding a leading zero — the form every
        // reader and every consumer already treats as hex. With an explicit format there is
        // nothing to encode into the value, so it is stored exactly as typed.
        rfid_tag: rfidTagFormat === 'auto' ? applyTagRadix(rawRfidTag, rfidRadix) : rawRfidTag,
        rfid_tag_format: rfidTagFormat,
        person_id: selectedPerson?.id,
        unit_id: preselectedUnitId || editingVehicle?.unit_id || selectedUnitOption?.value,
        owner_name: data.owner_type === 'company'
          ? data.owner_name
          : (selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}` : data.owner_name),
      };
      // If editing and user removed the photo without selecting a new one, tell backend to clear it
      if (editingVehicle && photoRemoved && !photoFile) {
        vehicleData.clear_photo = true;
      }

      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vehicleData),
      });

      if (response.ok) {
        // The plate is editable on an existing vehicle, and the camera stores plates as
        // independent entries — saving a new one does NOT replace the old, which would go on
        // opening the gate forever. Remove the previous plate explicitly.
        const previousPlate = (editingVehicle?.plate || '').trim().toUpperCase();
        const newPlate = vehicleData.plate as string;
        if (editingVehicle && previousPlate && previousPlate !== newPlate) {
          try {
            await revokeVehiclePlateFromLpr(customerId, editingVehicle as any, previousPlate);
          } catch (err: any) {
            // Non-fatal for the save, but never silent — a stale plate is physical access.
            toast.error(err?.message || `Não foi possível remover a placa anterior ${previousPlate} das câmeras LPR`);
          }
        }

        // Same problem on the tag side: the readers and the Guarita keep the OLD card
        // registered, so it goes on opening the gate until someone removes it by hand.
        const previousTag = (editingVehicle?.rfid_tag || '').trim();
        const newTag = (vehicleData.rfid_tag as string || '').trim();
        if (editingVehicle && previousTag && previousTag !== newTag) {
          try {
            await revokeVehicleTagFromDevices(customerId, editingVehicle as any, previousTag);
          } catch (err: any) {
            toast.error(err?.message || `Não foi possível remover a tag anterior ${previousTag} dos leitores`);
          }
        }

        // Upload photo if a new file was selected
        if (photoFile) {
          const savedVehicle = await response.clone().json().catch(() => null);
          const vehicleId = savedVehicle?.data?.id || editingVehicle?.id;
          if (vehicleId) {
            try {
              const formData = new FormData();
              formData.append('photo', photoFile);
              const photoRes = await fetchWithAuth(`/api/v1/customers/${customerId}/vehicles/${vehicleId}/photo`, {
                method: 'POST',
                body: formData,
              });
              // Non-fatal — the vehicle itself was saved — but the console.warn below never
              // fired for an HTTP rejection, so a dropped photo was invisible to the operator.
              await ensureOk(photoRes, 'Falha ao enviar a foto do veículo');
            } catch (photoErr: any) {
              console.warn('Photo upload failed:', photoErr);
              toast.error(photoErr?.message || 'Veículo salvo, mas houve erro ao enviar a foto');
            }
          }
        }
        if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
        toast.success(editingVehicle ? 'Veículo atualizado!' : 'Veículo cadastrado!');
        onSuccess();
        onClose();
      } else {
        // Same as the Veículos page: a 409 carries {code, message} and names the conflicting plate
        // or the vehicle an RFID tag collides with. Reading `errorData.error` dropped it and showed
        // a generic failure instead.
        throw await toHttpError(response, 'Erro ao salvar veículo');
      }
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      toast.error(error?.message || 'Erro ao salvar veículo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle ESC key - requires double-press to close
  useEffect(() => {
    if (!open || isSubmitting) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();

        if (escPressCount === 0) {
          // First ESC press
          setEscPressCount(1);
          toast('Pressione ESC novamente para fechar', {
            duration: 2000,
          });

          // Reset counter after 3 seconds
          if (escTimeoutRef.current) clearTimeout(escTimeoutRef.current);
          escTimeoutRef.current = setTimeout(() => {
            setEscPressCount(0);
          }, 3000);
        } else {
          // Second ESC press - close modal
          setEscPressCount(0);
          if (escTimeoutRef.current) {
            clearTimeout(escTimeoutRef.current);
            escTimeoutRef.current = null;
          }
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
      }
    };
  }, [open, onClose, escPressCount, isSubmitting]);

  // Custom styles for react-select (dark/light theme)
  const selectStyles = darkMode ? {
    control: (base: any) => ({
      ...base,
      backgroundColor: '#111827',
      borderColor: '#1f2937',
      minHeight: '38px',
      '&:hover': { borderColor: '#374151' }
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: '#111827',
      border: '1px solid #1f2937'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#1f2937' : '#111827',
      color: '#e5e7eb',
      '&:hover': { backgroundColor: '#1f2937' }
    }),
    singleValue: (base: any) => ({ ...base, color: '#e5e7eb' }),
    input: (base: any) => ({ ...base, color: '#e5e7eb' }),
    placeholder: (base: any) => ({ ...base, color: '#6b7280' })
  } : {
    control: (base: any) => ({
      ...base,
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      minHeight: '38px',
      '&:hover': { borderColor: '#9ca3af' }
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f3f4f6' : '#ffffff',
      color: '#111827',
      '&:hover': { backgroundColor: '#f3f4f6' }
    }),
    singleValue: (base: any) => ({ ...base, color: '#111827' }),
    input: (base: any) => ({ ...base, color: '#111827' }),
    placeholder: (base: any) => ({ ...base, color: '#9ca3af' })
  };

  if (!open) return null;

  // Theme-aware class helpers
  const bg = darkMode ? 'bg-gray-950' : 'bg-white';
  const bgInput = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const bgDropdown = darkMode ? 'bg-gray-900' : 'bg-white';
  const bgHover = darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100';
  const border = darkMode ? 'border-gray-800' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-gray-200' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const placeholder = darkMode ? 'placeholder-gray-500' : 'placeholder-gray-400';

  return createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[150]">
      <div className={cn(bg, 'border rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden', border)}>
        {/* Header */}
        <div className={cn('px-5 py-3.5 border-b flex items-center justify-between flex-shrink-0', border)}>
          <span className={cn('text-xs font-bold tracking-[0.25em] uppercase', textMuted)}>
            {editingVehicle ? 'Editar Veículo' : 'Novo Veículo'}
          </span>
          <button type="button" onClick={onClose} disabled={isSubmitting}
            className={cn('p-1.5 rounded-lg transition-colors', darkMode ? 'text-gray-600 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 flex">
            {/* Left rail — the vehicle's identity, the way VehicleDetailModal presents
                it: the photo, then the plate it belongs to. The form used to open with a
                full-width empty photo slab above everything else. */}
            <aside className={cn('w-[250px] flex-shrink-0 border-r overflow-y-auto p-4 space-y-3', border)}>
              {/* Photo banner — the shared slot, also used by the Cadastro page. */}
              <VehiclePhotoField
                photoUrl={photoPreviewUrl}
                plate={getValues('plate')}
                darkMode={darkMode}
                disabled={isSubmitting}
                onPhotoPicked={(file, preview) => {
                  if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
                  setPhotoFile(file);
                  setPhotoPreviewUrl(preview);
                  setPhotoRemoved(false);
                }}
                onRemove={() => {
                  if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
                  setPhotoFile(null);
                  setPhotoPreviewUrl(null);
                  setPhotoRemoved(true);
                }}
              />
              {/* Live preview of what was typed — the plate IS the vehicle, and the detail
                  modal shows it exactly this way. */}
              {watchedPlate.trim() ? (
                <div className="flex justify-center pt-1">
                  <LicensePlate plate={watchedPlate} size="sm" />
                </div>
              ) : null}
            </aside>
          <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="space-y-4">


            {/* Plate + RFID */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Placa *</label>
                <input {...register('plate', { required: 'Placa é obrigatória' })} type="text" placeholder="ABC-1234" disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none uppercase font-mono tracking-widest', bgInput, textPrimary, placeholder, errors.plate ? 'border-red-500' : `${border} focus:border-blue-500`)} />
                {errors.plate && <p className="mt-1 text-[10px] text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.plate.message}</p>}
              </div>
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Tag RFID</label>
                <div className="flex gap-2">
                  {/* The format the number is written in. The string itself cannot say —
                      "01019872" is Wiegand on one system and padded hex on another, naming two
                      different cards — so the operator states it instead of us guessing. */}
                  <select
                    aria-label="rfid-tag-format"
                    value={rfidTagFormat}
                    onChange={(e) => setRfidTagFormat(e.target.value as RfidTagFormat)}
                    disabled={isSubmitting}
                    className={cn('px-2 py-2 text-xs rounded-xl border focus:outline-none flex-shrink-0', bgInput, textPrimary, border, 'focus:border-blue-500')}
                  >
                    {RFID_TAG_FORMATS.map(f => (
                      <option key={f} value={f}>{RFID_TAG_FORMAT_LABELS[f]}</option>
                    ))}
                  </select>
                  <input {...register('rfid_tag', { pattern: { value: /^[a-zA-Z0-9,xX]{3,30}$/, message: 'Tag RFID inválido' } })} type="text" placeholder="Ex: 12345678" disabled={isSubmitting}
                    className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none', bgInput, textPrimary, placeholder, errors.rfid_tag ? 'border-red-500' : `${border} focus:border-blue-500`)} />
                </div>
                {errors.rfid_tag && <p className="mt-1 text-[10px] text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.rfid_tag.message}</p>}
                <p className={cn('mt-1 text-[10px] leading-snug', textMuted)}>{RFID_TAG_FORMAT_HINTS[rfidTagFormat]}</p>
                {rfidTagPreview && (
                  <p className={cn('mt-1 text-[10px] font-mono', textMuted)}>
                    HEX {rfidTagPreview.hex} · DEC {rfidTagPreview.dec} · WG {rfidTagPreview.wg} · ID {rfidTagPreview.id}
                  </p>
                )}
              </div>
            </div>

            {/* Only meaningful in auto mode: an explicit format has already answered it, and
                answering it again would pad a leading zero onto a value we were told how to read. */}
            {rfidTagFormat === 'auto' && (
              <TagRadixSelector
                name="vehicle-rfid-radix"
                value={watchedRfidTag}
                radix={rfidRadix}
                onChange={setRfidRadix}
                showError={submitAttempted}
                disabled={isSubmitting}
                darkMode={darkMode}
              />
            )}

            {/* Brand + Model + Color + Year */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Marca</label>
                <input {...register('brand')} type="text" placeholder="Fiat, VW, GM..." disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:border-blue-500', bgInput, textPrimary, placeholder, border)} />
              </div>
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Modelo</label>
                <input {...register('model')} type="text" placeholder="Uno, Gol, Onix..." disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:border-blue-500', bgInput, textPrimary, placeholder, border)} />
              </div>
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Cor</label>
                <input {...register('color')} type="text" placeholder="Preto, Branco..." disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:border-blue-500', bgInput, textPrimary, placeholder, border)} />
              </div>
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1', textMuted)}>Ano</label>
                <input {...register('year', { valueAsNumber: true, min: { value: 1900, message: 'Ano inválido' }, max: { value: new Date().getFullYear() + 1, message: 'Ano inválido' } })}
                  type="number" placeholder={String(new Date().getFullYear())} disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none', bgInput, textPrimary, placeholder, errors.year ? 'border-red-500' : `${border} focus:border-blue-500`)} />
                {errors.year && <p className="mt-1 text-[10px] text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.year.message}</p>}
              </div>
            </div>

            {/* Owner Type - hidden when person is preselected */}
            {!preselectedPerson && (
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-2', textMuted)}>Tipo de Proprietário</label>
                <div className="flex gap-1.5 flex-wrap">
                  {(customerType === 'condominio'
                    ? [{ value: 'resident', label: 'Morador' }, { value: 'visitor', label: 'Visitante' }, { value: 'service_provider', label: 'Prestador' }, { value: 'company', label: 'Empresa' }]
                    : [{ value: 'employee', label: 'Funcionário' }, { value: 'visitor', label: 'Visitante' }, { value: 'company', label: 'Empresa' }]
                  ).map(opt => (
                    <button key={opt.value} type="button" disabled={isSubmitting}
                      onClick={() => { setValue('owner_type', opt.value as any); setSelectedPerson(null); setSearchTerm(''); setSearchResults([]); setSelectedUnitOption(null); setSelectedDepartment(null); }}
                      className={cn('px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors',
                        watchedOwnerType === opt.value
                          ? 'bg-blue-600 text-white border-transparent'
                          : darkMode ? 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-200' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                      )}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Block/Unit/Department Filters (before owner selection) - hidden when person is preselected or unit is already locked */}
            {!preselectedPerson && !preselectedUnitId && watchedOwnerType !== 'company' && (
              <>
                {/* Condominio: Unit filter for residents (Rua/Número when applicable, Quadra/Lote or Bloco/Unidade otherwise) */}
                {customerType === 'condominio' && watchedOwnerType === 'resident' && (
                  <div>
                    <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1.5', textMuted)}>
                      {getUnitLabel(condoType)} (Opcional)
                    </label>
                    <UnitSelector
                      customerId={customerId}
                      value={selectedUnitOption}
                      onChange={(opt) => { setSelectedUnitOption(opt); setShowPersonSelector(false); setSearchResults([]); setSearchTerm(''); setSelectedPerson(null); setValue('person_id', undefined); }}
                      condoType={condoType}
                      darkMode={darkMode}
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                {/* Empresa: Department filter for employees */}
                {customerType === 'empresa' && watchedOwnerType === 'employee' && (
                  <div>
                    <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1.5', textMuted)}>

                      Departamento (Opcional)
                    </label>
                    <Select
                      options={departments.map(d => ({ value: d.id, label: d.name }))}
                      value={selectedDepartment}
                      onChange={setSelectedDepartment}
                      placeholder="Todos os departamentos"
                      isClearable
                      isDisabled={isSubmitting}
                      styles={selectStyles}
                      className="text-sm"
                    />
                  </div>
                )}
              </>
            )}

            {/* Owner Selection */}
            {watchedOwnerType === 'company' ? (
              // Company name input
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1.5', textMuted)}>Nome da Empresa</label>
                <input {...register('owner_name')} type="text" placeholder="Digite o nome da empresa" disabled={isSubmitting}
                  className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:border-blue-500', bgInput, textPrimary, placeholder, border)} />
              </div>
            ) : (
              // Person selector
              <div>
                <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1.5', textMuted)}>
                  Proprietário {(!preselectedUnitId && !editingVehicle?.unit_id && !selectedUnitOption && !selectedDepartment) ? '*' : '(opcional)'}
                  {!selectedPerson && submitAttempted && !preselectedUnitId && !editingVehicle?.unit_id && !selectedUnitOption && !selectedDepartment && (
                    <span className="text-red-400 text-xs font-semibold">(obrigatório)</span>
                  )}
                </label>
                {/* Unit context badge — shown when opening from a unit detail view */}
                {(preselectedUnitId || editingVehicle?.unit_id) && preselectedUnitName && (
                  <div className={cn('mb-2 flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs', darkMode ? 'bg-blue-900/20 border-blue-700/40 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700')}>
                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Vinculado à <strong>{preselectedUnitName}</strong> — proprietário é opcional</span>
                  </div>
                )}
                {selectedPerson ? (
                  // Show selected person
                  <div className={cn('flex items-center gap-3 p-3 rounded-lg border', bgInput, border)}>
                    {selectedPerson.identity_photo_url ? (
                      <img
                        src={makeAbsolutePhotoUrl(selectedPerson.identity_photo_url) ?? undefined}
                        alt={`${selectedPerson.first_name} ${selectedPerson.last_name}`}
                        className={cn('h-10 w-10 rounded-lg object-cover flex-shrink-0 border', darkMode ? 'border-gray-700' : 'border-gray-300')}
                      />
                    ) : (
                      <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-semibold', darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600')}>
                        {selectedPerson.first_name?.[0]?.toUpperCase()}{selectedPerson.last_name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm font-medium', textPrimary)}>
                        {selectedPerson.first_name} {selectedPerson.last_name}
                      </p>
                      {/* Where the owner actually lives. The form knew the unit well enough to
                          preselect the Quadra/Lote pickers but never showed the address, so an
                          operator editing a vehicle could not see it without leaving. Rua on
                          one row, quadra/lote on the next — same split as the card and the
                          detail modal. */}
                      {ownerUnitLines.street && (
                        <p title={ownerUnitLines.street} className={cn('text-xs truncate', textMuted)}>{ownerUnitLines.street}</p>
                      )}
                      {ownerUnitLines.unit && (
                        <p title={ownerUnitLines.unit} className={cn('text-xs truncate', textMuted)}>{ownerUnitLines.unit}</p>
                      )}
                      {selectedPerson.email && (
                        <p className={cn('text-xs truncate', textMuted)}>{selectedPerson.email}</p>
                      )}
                    </div>
                    {!preselectedPerson && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPerson(null);
                          setValue('person_id', undefined);
                          setShowPersonSelector(false);
                          setSearchResults([]);
                          setSearchTerm('');
                        }}
                        disabled={isSubmitting}
                        className={cn(textMuted, 'hover:text-red-400 transition-colors')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  // Show search input
                  <div className="relative">
                    <input
                      ref={proprietarioInputRef}
                      type="text"
                      placeholder={`Buscar ${
                        watchedOwnerType === 'resident' ? 'morador' :
                        watchedOwnerType === 'employee' ? 'funcionário' :
                        watchedOwnerType === 'service_provider' ? 'prestador' : 'visitante'
                      }...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => setShowPersonSelector(true)}
                      disabled={isSubmitting}
                      className={cn(
                        'w-full px-3 py-2 text-sm rounded-lg border focus:outline-none',
                        bgInput, textPrimary, placeholder,
                        !selectedPerson && submitAttempted && !preselectedUnitId && !editingVehicle?.unit_id && !selectedUnitOption && !selectedDepartment
                          ? 'border-red-500 focus:border-red-500'
                          : `${border} focus:border-blue-500`
                      )}
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                      </div>
                    )}
                    {/* Search results dropdown */}
                    {showPersonSelector && searchResults.length > 0 && (
                      <div className={cn('absolute top-full mt-1 w-full rounded-lg shadow-2xl max-h-60 overflow-y-auto z-20 border', bgDropdown, border)}>
                        {searchResults.map((person) => (
                          <button
                            key={person.id}
                            type="button"
                            onClick={async () => {
                              setSelectedPerson(person);
                              setValue('person_id', person.id);
                              setShowPersonSelector(false);
                              setSearchTerm('');

                              // Auto-select block and unit for residents
                              if (watchedOwnerType === 'resident' && customerType === 'condominio') {
                                try {
                                  console.log('[VehicleForm] Fetching person details for auto-select:', person.id);
                                  // Fetch full person details to get unit/block info
                                  const response = await fetchWithAuth(`/api/v1/people/${person.id}/full`);
                                  if (response.ok) {
                                    const data = await response.json();
                                    const personData = data.data || data;
                                    console.log('[VehicleForm] Person data:', personData);

                                    // Find resident role
                                    const residentRole = personData.roles?.find((r: any) => r.role_type === 'resident');
                                    console.log('[VehicleForm] Resident role:', residentRole);

                                    if (residentRole && residentRole.role_metadata) {
                                      const metadata = residentRole.role_metadata;
                                      console.log('[VehicleForm] Metadata:', metadata);

                                      // Auto-select unit if available (UnitSelector resolves block/street context itself)
                                      if (metadata.unit_id && metadata.unit_number) {
                                        const unitOption = { value: metadata.unit_id, label: metadata.unit_number, blockName: metadata.block_name };
                                        console.log('[VehicleForm] Auto-selecting unit:', unitOption);
                                        setSelectedUnitOption(unitOption);
                                      } else {
                                        console.log('[VehicleForm] No unit info in metadata');
                                      }
                                    } else {
                                      console.log('[VehicleForm] No resident role found');
                                    }
                                  }
                                } catch (error) {
                                  console.error('[VehicleForm] Error fetching person details for auto-select:', error);
                                }
                              }

                              // Auto-select department for employees
                              if (watchedOwnerType === 'employee' && customerType === 'empresa') {
                                try {
                                  const response = await fetchWithAuth(`/api/v1/people/${person.id}/full`);
                                  if (response.ok) {
                                    const data = await response.json();
                                    const personData = data.data || data;

                                    // Find employee role (backend may store as 'staff' or 'funcionario')
                                    const employeeRole = personData.roles?.find((r: any) => r.role_type === 'staff' || r.role_type === 'funcionario');
                                    if (employeeRole && employeeRole.role_metadata) {
                                      const metadata = employeeRole.role_metadata;

                                      // Auto-select department if available
                                      if (metadata.department_id && metadata.department_name) {
                                        const deptOption = { value: metadata.department_id, label: metadata.department_name };
                                        setSelectedDepartment(deptOption);
                                      }
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error fetching person details for auto-select:', error);
                                }
                              }
                            }}
                            className={cn('w-full flex items-center gap-3 p-3 transition-colors text-left', bgHover)}
                          >
                            {/* Avatar: photo or initials */}
                            {person.identity_photo_url ? (
                              <img
                                src={makeAbsolutePhotoUrl(person.identity_photo_url) ?? undefined}
                                alt={`${person.first_name} ${person.last_name}`}
                                className="h-8 w-[26px] rounded-md object-cover flex-shrink-0 border border-gray-700"
                              />
                            ) : (
                              <div className={cn('h-8 w-[26px] rounded-md flex items-center justify-center flex-shrink-0 text-xs font-semibold', darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600')}>
                                {person.first_name?.[0]?.toUpperCase()}{person.last_name?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={cn('text-sm font-medium', textPrimary)}>
                                {person.first_name} {person.last_name}
                              </p>
                              <p className={cn('text-xs truncate', textMuted)}>
                                {(person as any).unit_name
                                  ? (person as any).unit_name
                                  : person.email || ''}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* No results message */}
                    {showPersonSelector && (searchTerm.trim() || selectedUnitOption || selectedDepartment) && !isSearching && searchResults.length === 0 && (
                      <div className={cn('absolute top-full mt-1 w-full rounded-lg shadow-2xl p-3 z-20 border', bgDropdown, border)}>
                        <p className={cn('text-sm text-center', textMuted)}>
                          Nenhum resultado encontrado
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* Validation error for missing proprietario (only when no unit/block/dept context) */}
                {!selectedPerson && submitAttempted && !preselectedUnitId && !editingVehicle?.unit_id && !selectedUnitOption && !selectedDepartment && (
                  <p className="mt-2 text-xs text-red-400 flex items-center gap-1 bg-red-500/10 border border-red-500/20 rounded px-2 py-1.5">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="font-medium">Selecione o proprietário do veículo</span>
                  </p>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className={cn('block text-[10px] font-bold tracking-widest uppercase mb-1.5', textMuted)}>Observações</label>
              <textarea {...register('notes')} rows={2} placeholder="Observações sobre o veículo..." disabled={isSubmitting}
                className={cn('w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:border-blue-500 resize-none', bgInput, textPrimary, placeholder, border)} />
            </div>
          </div>
          </div>{/* end scrollable body */}
          </div>{/* end two-column row */}

          {/* Actions */}
          <div className={cn('flex items-center justify-end gap-2 px-5 py-3.5 border-t flex-shrink-0', border)}>
            <button type="button" onClick={onClose} disabled={isSubmitting}
              className={cn('px-4 py-2 text-sm font-medium rounded-xl border transition-colors disabled:opacity-50', bgInput, bgHover, textSecondary, border)}>
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingVehicle ? 'Salvar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>

      {/* Click outside to close (only when not submitting) */}
      {showPersonSelector && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowPersonSelector(false)}
        />
      )}
    </div>
  , document.body);
};
